import { keyframes } from "@emotion/react";
import {
  IgBasePrimitives,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { TypeAnimation } from "react-type-animation";

import { FloatingShapes } from "@src/features/getStarted/components/GetStartedHeroBackground";
import { GET_STARTED_GRADIENT } from "@src/features/getStarted/components/getStartedStyles";

const revealButton = keyframes`
  from { opacity: 0; pointer-events: none; }
  to   { opacity: 1; pointer-events: auto; }
`;

import { IntelligemsLockupHorizontalLogo } from "@src/components/IntelligemsLogo/IntelligemsLockupHorizontalLogo";
import { SineWaveDashedLine } from "@src/components/MainApp/Onboarding/components/SignWaveDashedLine";

interface OnboardingWelcomeProps {
  open: boolean;
  onGetStarted: () => void;
}

export const OnboardingWelcome = ({
  open,
  onGetStarted,
}: OnboardingWelcomeProps) => {
  return (
    <Dialog
      id="onboarding-welcome"
      open={open}
      fullScreen
      PaperProps={{
        sx: {
          background: GET_STARTED_GRADIENT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "fixed",
          marginLeft: 0,
          left: 0,
        },
      }}
    >
      <FloatingShapes />

      <Stack
        direction="column"
        spacing={8}
        alignItems="center"
        sx={{ position: "relative", zIndex: 1, maxWidth: 800, px: 4 }}
      >
        <Box sx={{ width: 178 }}>
          <IntelligemsLockupHorizontalLogo color="white" />
        </Box>

        <Typography
          component="h2"
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: 36,
            textAlign: "center",
            maxWidth: 728,
            lineHeight: 1.4,
          }}
        >
          {"You're just a couple steps away from your first "}
          <Box
            component="span"
            sx={{ textDecoration: "underline", textDecorationSkipInk: "none" }}
          >
            <TypeAnimation
              sequence={[
                "personalization",
                3000,
                "experience",
                3000,
                "test",
                3000,
              ]}
              speed={50}
              repeat={Infinity}
            />
          </Box>
          <Box component="span" sx={{ color: IgBasePrimitives[700] }}>
            {" ."}
          </Box>
        </Typography>

        <Typography
          sx={{
            color: "white",
            fontSize: "18px",
            lineHeight: "26px",
            textAlign: "center",
            fontWeight: 400,
          }}
        >
          Welcome to Intelligems. Run your first test or experience in minutes.
        </Typography>
        <Box sx={{ position: "absolute" }}>
          <SineWaveDashedLine opacity={0.2} />
        </Box>

        <Button
          onClick={onGetStarted}
          sx={{
            backgroundColor: IgGrayPrimitives[300],
            color: IgGrayPrimitives.A600,
            fontWeight: 600,
            fontSize: "14px",
            px: 4,
            height: 44,
            borderRadius: "6px",
            animation: `${revealButton} 0.3s ease-out 6s backwards`,
            "&:hover": { backgroundColor: IgGrayPrimitives[600] },
            "&:active": { backgroundColor: IgGrayPrimitives[700] },
          }}
        >
          Get started
        </Button>
      </Stack>
    </Dialog>
  );
};
